import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Clock,
  MapPin,
  Building2,
  Check,
  Users,
  Plane,
  Navigation,
  UserCheck,
  CreditCard,
  Zap,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingWidget from '../components/BookingWidget';
import { useTranslation } from 'react-i18next';

// Hero background - subtle gradient
const heroPattern = "data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E";

// Fleet images — local assets in frontend/public/images/fleet/.
// Mercedes E-Class for Business-Limousine, Mercedes V-Class for Premium-Van.
const fleetImages = {
  berline: "/images/fleet/business-sedan.png",
  van: "/images/fleet/premium-van.png"
};

const HomePage = () => {
  const { t } = useTranslation();
  const bookingRef = useRef(null);

  const scrollToBooking = () => {
    bookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Trust features - 4 cards (text via i18n)
  const trustFeatures = [
    { icon: UserCheck, key: 'professional' },
    { icon: CreditCard, key: 'transparent' },
    { icon: Zap, key: 'fastBooking' },
    { icon: Clock, key: 'available' },
  ];

  // Services - 3 cards (text via i18n)
  const services = [
    { icon: Plane, key: 'airport' },
    { icon: Navigation, key: 'city' },
    { icon: Users, key: 'private' },
  ];

  // Fleet vehicles (text via i18n; static image + spec count)
  const fleet = [
    { key: 'berline', image: fleetImages.berline, specCount: 4 },
    { key: 'van', image: fleetImages.van, specCount: 4 },
  ];

  // Coverage zones (place names — kept untranslated)
  const zones = [
    'Nantes Centre', 'Aéroport Nantes Atlantique', 'Gare SNCF',
    'La Baule', 'Saint-Nazaire', 'Pornic', 'Rennes', 'Angers'
  ];

  // KG-3R (Truth Before Reach): fabricated testimonials removed — no real review pipeline exists.

  return (
    <div className="min-h-screen bg-[#f8f8f8]" data-testid="home-page">
          <Seo {...seo.home} />

      {/* ========== HERO SECTION ========== */}
      <section
        className="relative min-h-[90vh] flex items-center pt-24 pb-16 overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%)',
          backgroundImage: `url("${heroPattern}")`,
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left Column - Hero text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <motion.span
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="inline-block px-4 py-2 bg-black text-white rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-6"
              >
                {t('hero.badge')}
              </motion.span>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-black tracking-tight leading-[1.1] mb-6">
                {t('hero.title1')}<br />
                <span className="text-neutral-500">{t('hero.title2')}</span>
              </h1>

              <p className="text-lg md:text-xl text-neutral-600 mb-8 max-w-lg leading-relaxed">
                {t('hero.description')}
              </p>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  onClick={scrollToBooking}
                  className="bg-black text-white hover:bg-neutral-800 px-8 py-6 text-base font-semibold rounded-xl shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/25"
                  data-testid="hero-book-btn"
                >
                  {t('hero.bookBtn')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>

              {/* KG-3R: fabricated star-rating trust badge removed (Truth Before Reach). */}
            </motion.div>

            {/* Right Column - Booking Widget */}
            <motion.div
              ref={bookingRef}
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-1 border border-neutral-100">
                <BookingWidget />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== SOCIAL PROOF COUNTER ========== */}
      <section className="py-12 md:py-16 bg-white border-b border-neutral-100" data-testid="social-proof-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          {/* KG-3R: fabricated 4.9/5 rating stat removed (Truth Before Reach). */}
          {/* Owner decision (Truth Before Reach): unverified "120+ rides" stat removed — no documented source. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Stat - Availability */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
              data-testid="stat-availability"
            >
              <div className="flex items-center justify-center gap-1 mb-2">
                <span className="text-3xl md:text-4xl font-heading font-bold text-black">24</span>
                <span className="text-xl md:text-2xl text-neutral-400 font-light">/7</span>
              </div>
              <p className="text-sm text-neutral-500">{t('home.stats.availability')}</p>
            </motion.div>

            {/* Stat 4 - Coverage */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
              data-testid="stat-coverage"
            >
              <div className="flex items-center justify-center mb-2">
                <span className="text-3xl md:text-4xl font-heading font-bold text-black">Nantes</span>
              </div>
              <p className="text-sm text-neutral-500">{t('home.stats.coverage')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== TRUST SECTION ========== */}
      <section className="py-20 md:py-28 bg-white" data-testid="trust-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-black tracking-tight mb-4">
              {t('home.trust.title')}
            </h2>
            <p className="text-neutral-500 max-w-2xl mx-auto text-lg">
              {t('home.trust.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustFeatures.map((feature, i) => (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-neutral-50 hover:bg-white border border-neutral-100 hover:border-neutral-200 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:shadow-neutral-100/50 hover:-translate-y-1"
                data-testid={`trust-card-${i}`}
              >
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-lg text-black mb-2">
                  {t(`home.trust.features.${feature.key}.title`)}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {t(`home.trust.features.${feature.key}.desc`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SERVICES SECTION ========== */}
      <section className="py-20 md:py-28 bg-[#f8f8f8]" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-black text-white rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-4">
              {t('home.services.badge')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-black tracking-tight">
              {t('home.services.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* WP-3.4 — homepage service teasers link to the /services hub (contextual internal
                link, previously dead cursor-pointer cards). Teaser keys (airport/city/private) are
                not 1:1 catalogue slugs, so all three route to the hub rather than inventing a
                slug mapping; the hub then routes to the specific service pages. */}
            {services.map((service, i) => (
              <Link
                key={service.key}
                to="/services"
                className="block"
                data-testid={`service-card-link-${i}`}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="group h-full bg-white border border-neutral-100 rounded-2xl p-8 hover:border-black/10 transition-all duration-500 hover:shadow-xl hover:shadow-neutral-200/50 hover:-translate-y-1 cursor-pointer"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-14 h-14 bg-neutral-100 group-hover:bg-black rounded-2xl flex items-center justify-center mb-6 transition-all duration-300">
                    <service.icon className="w-7 h-7 text-neutral-700 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-semibold text-xl text-black mb-3">
                    {t(`home.services.items.${service.key}.title`)}
                  </h3>
                  <p className="text-neutral-500 leading-relaxed">
                    {t(`home.services.items.${service.key}.desc`)}
                  </p>
                  <div className="mt-6 flex items-center text-black font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {t('home.services.learnMore')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FLEET SECTION ========== */}
      <section className="py-20 md:py-28 bg-white" data-testid="fleet-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-black text-white rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-4">
              {t('home.fleet.badge')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-black tracking-tight mb-4">
              {t('home.fleet.title')}
            </h2>
            <p className="text-neutral-500 max-w-2xl mx-auto text-lg">
              {t('home.fleet.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {fleet.map((vehicle, i) => (
              <motion.div
                key={vehicle.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="group bg-neutral-50 rounded-3xl overflow-hidden border border-neutral-100 hover:border-neutral-200 transition-all duration-500 hover:shadow-xl"
                data-testid={`fleet-card-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={vehicle.image}
                    alt={t(`home.fleet.${vehicle.key}.type`)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-xl text-black">
                        {t(`home.fleet.${vehicle.key}.type`)}
                      </h3>
                      <p className="text-neutral-500 text-sm">
                        {t(`home.fleet.${vehicle.key}.model`)}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-black bg-neutral-200 px-3 py-1.5 rounded-full">
                      {t(`home.fleet.${vehicle.key}.price`)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: vehicle.specCount }, (_, j) => (
                      <span
                        key={j}
                        className="inline-flex items-center gap-1.5 text-xs text-neutral-600 bg-white px-3 py-1.5 rounded-full border border-neutral-200"
                      >
                        <Check className="w-3 h-3 text-green-500" />
                        {t(`home.fleet.${vehicle.key}.specs${j + 1}`)}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ZONES SECTION ========== */}
      <section className="py-20 md:py-28 bg-[#f8f8f8]" data-testid="zones-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-black tracking-tight mb-4">
              {t('home.zonesSection.title')}
            </h2>
            <p className="text-neutral-500 max-w-2xl mx-auto">
              {t('home.zonesSection.subtitle')}
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-3">
            {zones.map((zone, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-2 bg-white border border-neutral-200 px-5 py-3 rounded-full hover:border-black/20 hover:shadow-md transition-all duration-300"
              >
                <MapPin className="w-4 h-4 text-neutral-400" />
                <span className="text-neutral-700 font-medium text-sm">{zone}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS SECTION ========== */}
      {/* KG-3R (Truth Before Reach): fabricated testimonials section removed — no real review
          pipeline exists. A genuine reviews section may be reintroduced only when backed by a
          verifiable review pipeline (ADR-0007 §12.7). */}

      {/* ========== CTA SECTION ========== */}
      <section className="py-20 md:py-28 bg-black relative overflow-hidden" data-testid="cta-section">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }} />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              {t('home.cta.title1')}<br />
              <span className="text-neutral-400">{t('home.cta.title2')}</span>
            </h2>
            <p className="text-neutral-400 max-w-xl mx-auto mb-10 text-lg">
              {t('home.cta.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={scrollToBooking}
                className="bg-white text-black hover:bg-neutral-100 px-10 py-6 text-base font-semibold rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                data-testid="cta-book-btn"
              >
                {t('home.cta.button')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <a href="tel:+33939242320" className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
                <span className="font-medium">09 39 24 23 20</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== ENTERPRISE CTA ========== */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-neutral-50 border border-neutral-200 rounded-3xl p-10 md:p-16 text-center"
          >
            <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-4">
              {t('home.enterpriseCta.title')}
            </h2>
            <p className="text-neutral-500 max-w-xl mx-auto mb-8">
              {t('home.enterpriseCta.subtitle')}
            </p>
            <Link to="/entreprises">
              <Button
                className="bg-black text-white hover:bg-neutral-800 px-8 py-6 text-base font-semibold rounded-xl shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-0.5"
                data-testid="enterprise-cta-btn"
              >
                {t('home.enterpriseCta.button')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
